import ListPageController from "./mixins/ListPageController";
import Fragment from "sap/ui/core/Fragment";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import type Dialog from "sap/m/Dialog";
import type { Select$ChangeEvent } from "sap/m/Select";
import type Context from "sap/ui/model/odata/v4/Context";
import type { Button$PressEvent } from "sap/m/Button";
import type { ResponseError } from "../types/Errors";
import { problemMessage } from "../util/ProblemDetails";

type StatusKey = "ALL" | "Active" | "Inactive";
type LocationRegion = "Global" | "Americas" | "Europe" | "AsiaPacific" | "MiddleEastAfrica";

interface LocationCreateDraft {
	code: string;
	name: string;
	region: LocationRegion;
	sortOrder: string;
}

interface LocationEditDraft {
	id: string;
	code: string;
	region: string;
	name: string;
	sortOrder: string;
}

interface ComponentVersion {
	version: string;
	revision: string;
}

interface SystemVersionResponse {
	portal: ComponentVersion;
	licenseServer: ComponentVersion | null;
}

interface VersionRow {
	name: string;
	version: string;
	revision: string;
}

interface ViewState {
	statusFilter: StatusKey;
	createDraft: LocationCreateDraft;
	editDraft: LocationEditDraft;
	versions: VersionRow[];
}

/** Commits are shown short, like git does; the full value stays in the API response. */
const SHORT_REVISION = 7;

const EMPTY_CREATE: LocationCreateDraft = {
	code: "",
	name: "",
	region: "Europe",
	sortOrder: "10"
};

const EMPTY_EDIT: LocationEditDraft = {
	id: "",
	code: "",
	region: "",
	name: "",
	sortOrder: ""
};

/**
 * System Info hub. Pierwszy tab: Locations admin CRUD (mirror logic z Locations.controller.ts —
 * świadoma duplikacja na poziomie handlerów, bo IconTabBar embedded tab nie pozwala łatwo
 * dzielić controllera z osobną Locations.view standalone. Refactor do mixin gdy dorzucimy
 * trzeci consumer.
 *
 * Second tab: versions of the portal and the License Server (card 30). Further tabs (permission catalog,
 * audit retention) come as more IconTabFilters with handlers here or in a per-tab mixin.
 *
 * @namespace computec.b2b.portal.controller
 */
export default class SystemInfo extends ListPageController {
	public override onInit(): void {
		const initial: ViewState = {
			statusFilter: "ALL",
			createDraft: { ...EMPTY_CREATE },
			editDraft: { ...EMPTY_EDIT },
			versions: []
		};
		this.getView().setModel(new JSONModel(initial), "view");
		this.getRouter().getRoute("systemInfo")?.attachPatternMatched(() => { void this.onShow(); });
	}

	private async onShow(): Promise<void> {
		const bundle = await this.getResourceBundle();
		this.setPageHeader({
			title: bundle.getText("nav.systemInfo") ?? "System Info",
			subtitle: bundle.getText("systemInfo.subtitle") ?? ""
		});
		await this.loadVersions();
	}

	/** Portal and License Server versions; an unavailable LS shows as such, a failed call leaves the table empty. */
	private async loadVersions(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const model = this.getView().getModel("view") as JSONModel;
		const unavailable = bundle.getText("systemInfo.versions.unavailable") ?? "";
		try {
			const result = await this.getOwnerComponent().getAuthFetch().json<SystemVersionResponse>("/api/v1/system/version");
			const rows: VersionRow[] = [
				{
					name: bundle.getText("systemInfo.versions.portal") ?? "",
					version: result.portal.version || unavailable,
					revision: result.portal.revision.slice(0, SHORT_REVISION)
				},
				{
					name: bundle.getText("systemInfo.versions.licenseServer") ?? "",
					version: result.licenseServer?.version ?? unavailable,
					revision: result.licenseServer?.revision.slice(0, SHORT_REVISION) ?? ""
				}
			];
			model.setProperty("/versions", rows);
		} catch {
			model.setProperty("/versions", []);
		}
	}

	public onStatusSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as StatusKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/statusFilter", key);
		this.applyFilters();
	}

	private applyFilters(): void {
		const state = (this.getView().getModel("view") as JSONModel).getData() as ViewState;
		const parts: string[] = [];
		if (state.statusFilter !== "ALL") {
			parts.push(`Status eq '${state.statusFilter}'`);
		}
		const binding = this.getTableBinding("locationsTable");
		binding?.changeParameters({ $filter: parts.length > 0 ? parts.join(" and ") : undefined });
	}

	public onRefresh(): void {
		this.refreshTable("locationsTable");
	}

	// === Create dialog ===

	private createDialog?: Dialog;

	public async onAddLocation(): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty("/createDraft", { ...EMPTY_CREATE });

		if (!this.createDialog) {
			this.createDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.LocationCreateDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.createDialog);
		}
		this.createDialog.open();
	}

	public onCancelCreateLocation(): void {
		this.createDialog?.close();
	}

	public async onSubmitCreateLocation(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const draft = (this.getView().getModel("view") as JSONModel).getProperty("/createDraft") as LocationCreateDraft;

		const code = draft.code.trim();
		const name = draft.name.trim();
		if (code.length === 0 || name.length === 0) {
			MessageBox.warning(bundle.getText("locations.add.error.fieldsRequired") ?? "Code and Name are required.");
			return;
		}
		const sortOrder = Number(draft.sortOrder);
		if (!Number.isFinite(sortOrder)) {
			MessageBox.warning(bundle.getText("locations.add.error.sortOrderInvalid") ?? "SortOrder must be a number.");
			return;
		}

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json("/api/v1/locations", {
				method: "POST",
				body: JSON.stringify({ code, name, region: draft.region, sortOrder })
			});
			this.createDialog?.close();
			MessageToast.show(bundle.getText("locations.add.success") ?? "Location added.");
			this.refreshTable("locationsTable");
		} catch (e) {
			MessageBox.error(problemMessage((e as ResponseError).bodyText, bundle.getText("locations.add.failure") ?? "Failed to add."));
		}
	}

	// === Edit dialog ===

	private editDialog?: Dialog;

	public async onEditLocation(event: Button$PressEvent): Promise<void> {
		const ctx = event.getSource().getBindingContext("odata") as Context | null;
		if (!ctx) {
			return;
		}
		const draft: LocationEditDraft = {
			id: ctx.getProperty("Id") as string,
			code: (ctx.getProperty("Code") as string) ?? "",
			region: (ctx.getProperty("Region") as string) ?? "",
			name: (ctx.getProperty("Name") as string) ?? "",
			sortOrder: String((ctx.getProperty("SortOrder") as number) ?? 0)
		};
		(this.getView().getModel("view") as JSONModel).setProperty("/editDraft", draft);

		if (!this.editDialog) {
			this.editDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.LocationEditDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.editDialog);
		}
		this.editDialog.open();
	}

	public onCancelEditLocation(): void {
		this.editDialog?.close();
	}

	public async onSubmitEditLocation(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const draft = (this.getView().getModel("view") as JSONModel).getProperty("/editDraft") as LocationEditDraft;

		const name = draft.name.trim();
		if (name.length === 0) {
			MessageBox.warning(bundle.getText("locations.add.error.fieldsRequired") ?? "Name is required.");
			return;
		}
		const sortOrder = Number(draft.sortOrder);
		if (!Number.isFinite(sortOrder)) {
			MessageBox.warning(bundle.getText("locations.add.error.sortOrderInvalid") ?? "SortOrder must be a number.");
			return;
		}

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/locations/${draft.id}/basics`, {
				method: "POST",
				body: JSON.stringify({ name, sortOrder })
			});
			this.editDialog?.close();
			MessageToast.show(bundle.getText("locations.edit.success") ?? "Location updated.");
			this.refreshTable("locationsTable");
		} catch (e) {
			MessageBox.error(problemMessage((e as ResponseError).bodyText, bundle.getText("locations.edit.failure") ?? "Failed."));
		}
	}

	// === Activate / Deactivate ===

	public async onActivateLocation(event: Button$PressEvent): Promise<void> {
		const ctx = event.getSource().getBindingContext("odata") as Context | null;
		if (!ctx) return;
		const id = ctx.getProperty("Id") as string;
		const bundle = await this.getResourceBundle();
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/locations/${id}/activate`, { method: "POST", body: "{}" });
			MessageToast.show(bundle.getText("locations.activate.success") ?? "Activated.");
			this.refreshTable("locationsTable");
		} catch (e) {
			MessageBox.error(problemMessage((e as ResponseError).bodyText, "Failed."));
		}
	}

	public async onDeactivateLocation(event: Button$PressEvent): Promise<void> {
		const ctx = event.getSource().getBindingContext("odata") as Context | null;
		if (!ctx) return;
		const id = ctx.getProperty("Id") as string;
		const bundle = await this.getResourceBundle();
		const reason = prompt(bundle.getText("locations.deactivate.prompt") ?? "Reason (min 1 char):", "manual");
		if (!reason || reason.trim().length === 0) return;
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/locations/${id}/deactivate`, {
				method: "POST",
				body: JSON.stringify({ reason })
			});
			MessageToast.show(bundle.getText("locations.deactivate.success") ?? "Deactivated.");
			this.refreshTable("locationsTable");
		} catch (e) {
			MessageBox.error(problemMessage((e as ResponseError).bodyText, "Failed."));
		}
	}
}

